import { useState } from 'react';

import { bulkLockIndirectTaxFilingsV2 } from '@commenda/api/indirect-tax/filings';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';

interface IndirectTaxFilingsBulkApproveModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingIds: string[];
  corporationId: number;
  onApproved?: () => void;
}

const IndirectTaxFilingsBulkApproveModal = ({
  isOpen,
  onClose,
  filingIds,
  corporationId,
  onApproved,
}: IndirectTaxFilingsBulkApproveModalProps) => {
  const [isApproving, setIsApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = filingIds.length;
  const filingLabel = count === 1 ? 'filing' : 'filings';

  const handleApprove = async () => {
    setIsApproving(true);
    setError(null);
    const result = await bulkLockIndirectTaxFilingsV2({
      params: { corporationId, filingIds },
    });
    setIsApproving(false);
    if (result.success) {
      onApproved?.();
      onClose();
    } else {
      setError(result.error ?? 'Failed to approve filings');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader className='border-none flex-col items-start gap-2'>
          <DialogTitle>Approve {count} {filingLabel}?</DialogTitle>
          <DialogDescription>
            Once approved, the Commenda team will submit these {filingLabel} on your
            behalf. You will not be able to edit the transactions for these
            periods after approval.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='border-none'>
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleApprove}
            disabled={isApproving || count === 0}
          >
            {isApproving ? 'Approving...' : `Approve ${count} ${filingLabel}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { IndirectTaxFilingsBulkApproveModal };
